"use client";

import type { SettingsState } from "src/components/settings";
import type { Theme, Components , SupportedColorScheme } from "@mui/material/styles";

import { createTheme as createMuiTheme } from "@mui/material/styles";

import { mixins } from "./core/mixins";
import { shadows } from "./core/shadows";
import { palette } from "./core/palette";
import { themeConfig } from "./theme-config";
import { components } from "./core/components";
import { typography } from "./core/typography";
import { customShadows } from "./core/custom-shadows";
import { updateCoreWithSettings, updateComponentsWithSettings } from "./with-settings";

import type { ThemeOptions, ThemeColorScheme } from "./types";

// ----------------------------------------------------------------------

export const baseTheme: ThemeOptions = {
  colorSchemes: {
    light: {
      palette: palette.light,
      shadows: shadows.light,
      customShadows: customShadows.light,
    },
    dark: {
      palette: palette.dark,
      shadows: shadows.dark,
      customShadows: customShadows.dark,
    },
  },
  mixins,
  components,
  typography,
  shape: { borderRadius: 8 },
  direction: themeConfig.direction,
  cssVariables: themeConfig.cssVariables,
  // v7 tipa defaultColorScheme como 'light' | 'dark' (sem 'system'); o valor
  // efetivo é recalculado em createTheme() abaixo via resolveDefaultColorScheme.
  defaultColorScheme: themeConfig.defaultMode === "system" ? undefined : themeConfig.defaultMode,
};

// ----------------------------------------------------------------------

type CreateThemeProps = {
  settingsState?: SettingsState;
  themeOverrides?: ThemeOptions;
  localeComponents?: { components?: Components<Theme> };
};

type MuiBaseThemeInput = Parameters<typeof createMuiTheme>[0];

export function createTheme({
  settingsState,
  themeOverrides = {},
  localeComponents = {},
}: CreateThemeProps = {}): Theme {
  // Update core theme settings
  const updatedCore = settingsState ? updateCoreWithSettings(baseTheme, settingsState) : baseTheme;

  // Update component settings
  const updatedComponents = settingsState
    ? updateComponentsWithSettings(components, settingsState)
    : {};

  // Create and return the final theme
  const resolveDefaultColorScheme = (value?: ThemeColorScheme): SupportedColorScheme | undefined =>
    value && value !== "system" ? value : undefined;

  // Prefer configuração persistida (cookie) para o modo padrão; evita diferença SSR/cliente em Emotion
  const userScheme = settingsState?.colorScheme;
  const mergedColorScheme: ThemeColorScheme = userScheme ?? updatedCore.defaultColorScheme ?? themeConfig.defaultMode;

  const themeInput = {
    ...updatedCore,
    defaultColorScheme:
      resolveDefaultColorScheme(mergedColorScheme) ??
      resolveDefaultColorScheme(themeConfig.defaultMode) ??
      "dark",
  } as MuiBaseThemeInput;

  const theme = createMuiTheme(themeInput, updatedComponents, localeComponents, themeOverrides);

  return theme;
}
